import { useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  KeywordFeedbackCategory,
  type KeywordFeedbackDetails,
  KeywordFeedbackSubCategory,
} from "reedy-data/api";
import { toast } from "utils/toast";
import type { KeywordFeedbackEntry } from "../../hooks/useKeywordFeedback";
import {
  getIconAria,
  getIconTooltip,
  getPopoverHeader,
  type IconState,
  type PopoverHeader,
} from "./KeywordFeedbackPopover.labels";

export const MAX_FREE_TEXT_LENGTH = 150;

type Mode = "summary" | "editor";

export type FeedbackView =
  | { kind: "summary"; details: KeywordFeedbackDetails }
  | {
      kind: "categoryPicker";
      hoveredCategory: KeywordFeedbackCategory | null;
    }
  | { kind: "subReasonPicker"; category: KeywordFeedbackCategory }
  | {
      kind: "freeTextForm";
      category: KeywordFeedbackCategory;
      isSubOther: boolean;
      freeText: string;
    };

export interface UseKeywordFeedbackPopoverStateArgs {
  entry: KeywordFeedbackEntry | undefined;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (details?: KeywordFeedbackDetails) => Promise<void>;
  onDiscard: () => Promise<void>;
}

export interface UseKeywordFeedbackPopoverState {
  view: FeedbackView;
  header: PopoverHeader;
  isFlagged: boolean;
  trigger: {
    tooltipText?: string;
    ariaText: string;
    onClick: () => void;
  };
  onOpenChange: (open: boolean) => void;
  onHoverCategory: (category: KeywordFeedbackCategory | null) => void;
  onCategoryClick: (category: KeywordFeedbackCategory) => void;
  onSubCategoryClick: (
    subCategory: KeywordFeedbackSubCategory,
  ) => Promise<void>;
  onSubOtherSelect: () => void;
  onFreeTextChange: (value: string) => void;
  onFreeTextSubmit: () => Promise<void>;
  onBack: () => void;
  onEdit: () => void;
  onRemove: () => Promise<void>;
}

const derivePartialDetails = ({
  selectedCategory,
  subOtherMode,
  freeText,
}: {
  selectedCategory: KeywordFeedbackCategory | null;
  subOtherMode: boolean;
  freeText: string;
}): KeywordFeedbackDetails | undefined => {
  if (selectedCategory === null) return undefined;
  const trimmed = freeText.trim();
  const text = trimmed.length > 0 ? trimmed : undefined;
  if (subOtherMode) {
    return {
      category: selectedCategory,
      subCategory: KeywordFeedbackSubCategory.OTHER,
      freeText: text,
    };
  }
  if (selectedCategory === KeywordFeedbackCategory.OTHER) {
    return { category: KeywordFeedbackCategory.OTHER, freeText: text };
  }
  return { category: selectedCategory };
};

export const useKeywordFeedbackPopoverState = ({
  entry,
  isOpen,
  onOpenChange,
  onSubmit,
  onDiscard,
}: UseKeywordFeedbackPopoverStateArgs): UseKeywordFeedbackPopoverState => {
  const { t } = useTranslation(["components"]);
  const committedDetails = entry?.details;
  const isFlagged = entry !== undefined && !entry.discarded;
  const hasCommittedDetails = committedDetails !== undefined;

  const [mode, setMode] = useState<Mode>(
    hasCommittedDetails ? "summary" : "editor",
  );

  const [selectedCategory, setSelectedCategory] =
    useState<KeywordFeedbackCategory | null>(null);
  const [subOtherMode, setSubOtherMode] = useState(false);
  const [freeText, setFreeText] = useState("");
  const [hoveredCategory, setHoveredCategory] =
    useState<KeywordFeedbackCategory | null>(null);

  // Suppress duplicate "thank you" toasts within a single popover session.
  const toastShownRef = useRef(false);

  const partialCommittedRef = useRef(false);

  const resetEditor = useCallback(() => {
    setSelectedCategory(null);
    setSubOtherMode(false);
    setFreeText("");
    setHoveredCategory(null);
  }, []);

  const showThankYou = useCallback(() => {
    if (toastShownRef.current) return;
    toastShownRef.current = true;
    toast.success(t("components:keywordsTab.keywordFeedbackPopover.thankYou"));
  }, [t]);

  const commitPartialIfNeeded = useCallback(() => {
    if (mode !== "editor") return;
    // Editing existing details means the user already has a full submission
    // on file. Auto-committing a partial on dismiss would clobber it; leave
    // their prior feedback intact and let an explicit Submit overwrite.
    if (hasCommittedDetails) return;
    const partial = derivePartialDetails({
      selectedCategory,
      subOtherMode,
      freeText,
    });
    if (!partial) return;
    if (partialCommittedRef.current) return;
    partialCommittedRef.current = true;
    onSubmit(partial)
      .then(showThankYou)
      .catch(() => {
        /* hook surfaces the error toast */
      });
  }, [
    mode,
    hasCommittedDetails,
    selectedCategory,
    subOtherMode,
    freeText,
    onSubmit,
    showThankYou,
  ]);

  const handleIconClick = useCallback(() => {
    if (isOpen) {
      commitPartialIfNeeded();
      onOpenChange(false);
      return;
    }
    toastShownRef.current = false;
    partialCommittedRef.current = false;
    setMode(hasCommittedDetails ? "summary" : "editor");
    resetEditor();
    onOpenChange(true);
    // Fire the empty thumbs-down PUT in the background — the hook surfaces
    // any error toast and rolls back the optimistic entry. We open the
    // popover synchronously so the categorization flow feels instant.
    if (!isFlagged) {
      onSubmit(undefined)
        .then(showThankYou)
        .catch(() => {
          /* hook handles the error toast */
        });
    }
  }, [
    isOpen,
    isFlagged,
    hasCommittedDetails,
    onOpenChange,
    onSubmit,
    showThankYou,
    resetEditor,
    commitPartialIfNeeded,
  ]);

  const handleEdit = useCallback(() => {
    if (committedDetails) {
      setSelectedCategory(committedDetails.category);
      setSubOtherMode(
        committedDetails.subCategory === KeywordFeedbackSubCategory.OTHER,
      );
      setFreeText(committedDetails.freeText ?? "");
    } else {
      resetEditor();
    }
    setMode("editor");
    toastShownRef.current = false;
  }, [committedDetails, resetEditor]);

  const handleCategoryClick = useCallback(
    (category: KeywordFeedbackCategory) => {
      if (selectedCategory === category) {
        setSelectedCategory(null);
        return;
      }
      setSelectedCategory(category);
      setSubOtherMode(false);
    },
    [selectedCategory],
  );

  const handleSubCategoryClick = useCallback(
    async (subCategory: KeywordFeedbackSubCategory) => {
      if (!selectedCategory) return;
      try {
        await onSubmit({ category: selectedCategory, subCategory });
        showThankYou();
        onOpenChange(false);
      } catch {
        // toast handled in the hook; keep popover open so the user can retry.
      }
    },
    [selectedCategory, onSubmit, showThankYou, onOpenChange],
  );

  const handleSubOtherSelect = useCallback(() => setSubOtherMode(true), []);

  const handleFreeTextSubmit = useCallback(async () => {
    const category = selectedCategory ?? KeywordFeedbackCategory.OTHER;
    const subCategory = subOtherMode
      ? KeywordFeedbackSubCategory.OTHER
      : undefined;
    const trimmed = freeText.trim();
    try {
      await onSubmit({
        category,
        subCategory,
        freeText: trimmed.length > 0 ? trimmed : undefined,
      });
      showThankYou();
      onOpenChange(false);
    } catch {
      // hook surfaces the error toast
    }
  }, [
    selectedCategory,
    subOtherMode,
    freeText,
    onSubmit,
    showThankYou,
    onOpenChange,
  ]);

  const handleRemove = useCallback(async () => {
    try {
      await onDiscard();
    } catch {
      return;
    }
    onOpenChange(false);
    toast.success(t("components:keywordsTab.keywordFeedbackPopover.removed"));
  }, [onDiscard, onOpenChange, t]);

  const handleBack = useCallback(() => {
    if (subOtherMode) {
      setSubOtherMode(false);
      setFreeText("");
      return;
    }
    setSelectedCategory(null);
  }, [subOtherMode]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      onOpenChange(open);
      if (open) return;
      // Commit whatever partial selection the user made before dismissing so
      // the next open lands on the summary card and the BE log captures the
      // partial signal (Scenario 6).
      commitPartialIfNeeded();
      if (hasCommittedDetails) {
        setMode("summary");
      }
    },
    [commitPartialIfNeeded, hasCommittedDetails, onOpenChange],
  );

  const handleFreeTextChange = useCallback((value: string) => {
    setFreeText(value.slice(0, MAX_FREE_TEXT_LENGTH));
  }, []);

  const view: FeedbackView = useMemo(() => {
    if (mode === "summary" && committedDetails) {
      return { kind: "summary", details: committedDetails };
    }
    if (selectedCategory === null) {
      return { kind: "categoryPicker", hoveredCategory };
    }
    if (selectedCategory === KeywordFeedbackCategory.OTHER || subOtherMode) {
      return {
        kind: "freeTextForm",
        category: selectedCategory,
        isSubOther: subOtherMode,
        freeText,
      };
    }
    return { kind: "subReasonPicker", category: selectedCategory };
  }, [
    mode,
    committedDetails,
    selectedCategory,
    subOtherMode,
    freeText,
    hoveredCategory,
  ]);

  const header = useMemo(
    () =>
      getPopoverHeader({
        t,
        isSummary: view.kind === "summary",
        selectedCategory,
        isFreeTextStage: view.kind === "freeTextForm",
      }),
    [t, view.kind, selectedCategory],
  );

  const iconState: IconState = ((): IconState => {
    if (!isFlagged) return "flag";
    return hasCommittedDetails ? "view" : "unflag";
  })();

  return {
    view,
    header,
    isFlagged,
    trigger: {
      tooltipText: !isOpen ? getIconTooltip(t, iconState) : undefined,
      ariaText: getIconAria(t, iconState),
      onClick: handleIconClick,
    },
    onOpenChange: handleOpenChange,
    onHoverCategory: setHoveredCategory,
    onCategoryClick: handleCategoryClick,
    onSubCategoryClick: handleSubCategoryClick,
    onSubOtherSelect: handleSubOtherSelect,
    onFreeTextChange: handleFreeTextChange,
    onFreeTextSubmit: handleFreeTextSubmit,
    onBack: handleBack,
    onEdit: handleEdit,
    onRemove: handleRemove,
  };
};
